import { Injectable } from '@angular/core';
import { Observable, Subject, catchError, map, of, tap, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AppEnvironmentConfig } from 'projects/config/model/environment.config.model';
import { SvcHttpClient } from 'projects/lib-shared-common/src/public-api';
import { CommonCounter, CommonIndicator } from '@solvace/core/models';
import { InspectionIndicator } from './models/inspection-indicator';
import { InspetionCategorizedDrillDown } from './models/inspection-drilldown';
import { SvcCilRegisterLink } from './models/svc-cil-register-link.interface';
import { SvcRegistersLink } from '../../../interfaces/svc-register-link.interface';
import { TasksCategory } from 'projects/lib-shared-core/src/lib/features/main-tasks/models/tasks-category';
import { CheckinFilterService } from '../../../services/checkin-filter/checkin-filter.service';

@Injectable({ providedIn: 'root' })
export class CilAnalyticsService extends SvcHttpClient {
  private _indicator: Subject<InspectionIndicator> = new Subject<InspectionIndicator>();
  private _approvalsIndicator: Subject<CommonCounter[]> = new Subject<CommonCounter[]>();
  private _subordinatesIndicator: Subject<InspectionIndicator> = new Subject<InspectionIndicator>();
  private _categorizedDrillDown: Subject<InspetionCategorizedDrillDown[]> = new Subject<InspetionCategorizedDrillDown[]>();

  public _indicator$: Observable<InspectionIndicator> = this._indicator.asObservable();
  public _subordinatesIndicator$: Observable<InspectionIndicator> = this._subordinatesIndicator.asObservable();
  public _approvalsIndicator$: Observable<CommonCounter[]> = this._approvalsIndicator.asObservable();
  public _categorizedDrillDown$: Observable<InspetionCategorizedDrillDown[]> = this._categorizedDrillDown.asObservable();

  constructor(appConfig: AppEnvironmentConfig, httpClient: HttpClient, private _checkinFilterService: CheckinFilterService) {
    super(appConfig.APIs.apiUrlCil, httpClient);
  }

  getInspectionIndicators(): Observable<InspectionIndicator> {
    this._indicator.next(null);
    return this.get<CommonIndicator>('/Counters/inspections', {
      params: this._checkinFilterService.buildHttpParams(),
    }).pipe(
      map((response: CommonIndicator) => new InspectionIndicator(response)),
      tap((indicator) => {
        this._indicator.next(indicator);
      }),
      catchError((error) => {
        this._indicator.next(new InspectionIndicator({ hasError: true }));
        return throwError(() => error);
      })
    );
  }

  getInspectionSubordinatesIndicators(): Observable<InspectionIndicator> {
    this._indicator.next(null);
    return this.get<CommonIndicator>('/Counters/inspections/subordinates').pipe(
      map((response: CommonIndicator) => new InspectionIndicator(response)),
      tap((indicator) => {
        this._subordinatesIndicator.next(indicator);
      }),
      catchError((error) => {
        this._indicator.next(new InspectionIndicator({ hasError: true }));
        return throwError(() => error);
      })
    );
  }

  getInspectionApprovalsIndicator(): Observable<CommonCounter[]> {
    return this.get<CommonCounter[]>('/Counters/inspections/approvals', {
      params: this._checkinFilterService.buildHttpParams(),
    }).pipe(
      tap((indicator) => {
        this._approvalsIndicator.next(indicator);
      })
    );
  }

  getInspectionCategorizedDrillDown(type: TasksCategory): Observable<InspetionCategorizedDrillDown[]> {
    return this.get<InspetionCategorizedDrillDown[]>(`/Analytics/inspections/details/type`, {
      params: this._checkinFilterService.buildHttpParams({ indicadorTypeDetail: type }),
    }).pipe(
      map((response: InspetionCategorizedDrillDown[]) => response),
      tap((indicator) => {
        this._categorizedDrillDown.next(indicator);
      })
    );
  }

  getInspectionCategorizedDrillDownSubordinates(type: TasksCategory): Observable<InspetionCategorizedDrillDown[]> {
    return this.get<InspetionCategorizedDrillDown[]>(`/Analytics/subordinates/inspections/details/type?indicadorTypeDetail=${type}`).pipe(
      map((response: InspetionCategorizedDrillDown[]) => response),
      tap((indicator) => {
        this._categorizedDrillDown.next(indicator);
      })
    );
  }

  getRegistersLink(generalSearch: string): Observable<SvcCilRegisterLink[]> {
    return this.get<SvcCilRegisterLink[]>(`/simpleSearch?generalSearch=${generalSearch}`);
  }

  getRegisterLinkDataGeneric(generalSearch: string): Observable<SvcRegistersLink[]> {
    return this.getRegistersLink(generalSearch).pipe(
      map((links: SvcCilRegisterLink[]) => {
        return links?.map((link) => ({
          id: link.checklistId,
          name: link.checklistName,
          siteId: link.siteId,
          siteName: link.siteName,
          moduleAlias: link.moduleAlias,
          jsonCallback: link.jsonCallback,
        }));
      })
    );
  }

}
